import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './header.module.scss';
import useWebView from '../../hooks/useWebView';
import Logo from './logo/logo';
import HamburgerIcon from './images/hamburger-menu.svg';
import CloseIcon from './images/close.svg';
import DropdownArrow from './images/dropdown-arrow.svg';
import Dropdown from './dropdown/dropdown';
import SignIn from './signIn/signIn';
import trackHeapEvent from '../../services/helpers/trackHeapEvent';

const ICON_DIMENSIONS = {
  CLOSE_WIDTH: 22,
  CLOSE_HEIGHT: 22,
  HAMBURGER_WIDTH: 32,
  HAMBURGER_HEIGHT: 23,
};

export default function Header({
  logo = {},
  mobileLogo = {},
  navigation = [],
  signInData = {},
  theme = null,
  useSlimHeader = false,
}) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = React.useState(null);
  const [isSignInDropdownOpen, setIsSignInDropdownOpen] = React.useState(false);
  const closeTimeoutRef = React.useRef(null);
  const graceTimeoutRef = React.useRef(null);
  const navRefs = React.useRef([]);
  const openedViaKeyboard = React.useRef(false);
  const justOpenedRef = React.useRef(false);
  const isWebView = useWebView();

  React.useEffect(() => {
    return () => {
      clearTimeout(closeTimeoutRef.current);
      clearTimeout(graceTimeoutRef.current);
    };
  }, []);

  if (isWebView) {
    return null;
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMobileMenuClick = (label) => {
    if (label) {
      trackHeapEvent(
        { Name: 'Global Nav Header', CTA: label.replace(/ /g, '_') },
        'CMS_Unification'
      );
    }
    setIsMenuOpen(false);
    setActiveMobileSubmenu(null);
  };

  const handleNavEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleNavHover = (index) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (graceTimeoutRef.current) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    openedViaKeyboard.current = false;

    // Immediately switch to new dropdown without delay
    setActiveDropdown(index);

    justOpenedRef.current = true;
    graceTimeoutRef.current = setTimeout(() => {
      justOpenedRef.current = false;
    }, 200);
  };

  const handleNavLeave = () => {
    // Clear any existing timeout before setting a new one
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  const handleDropdownItemClick = (label) => {
    if (label) {
      trackHeapEvent(
        { Name: 'Global Nav Header', CTA: label.replace(/ /g, '_') },
        'CMS_Unification'
      );
    }
  };

  const handleNavKeyDown = (e, index, hasDropdown) => {
    if (!hasDropdown) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openedViaKeyboard.current = true;
      setActiveDropdown(activeDropdown === index ? null : index);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      openedViaKeyboard.current = false;
      setActiveDropdown(null);
      if (navRefs.current[index]) {
        navRefs.current[index].focus();
      }
    } else if (e.key === 'ArrowDown' && activeDropdown !== index) {
      e.preventDefault();
      openedViaKeyboard.current = true;
      setActiveDropdown(index);
    }
  };

  const isDropdownOpen = activeDropdown !== null;

  return (
    <>
      <div className={styles.gradientBar} />

      {(isDropdownOpen || isSignInDropdownOpen) && (
        <div className={styles.pageOverlay} aria-hidden="true" />
      )}

      <header
        className={classNames(styles.header, {
          [styles.headerSlim]: useSlimHeader,
          [styles.headerMenuOpen]: isMenuOpen || isDropdownOpen,
          [styles.signInOpen]: isSignInDropdownOpen,
        })}
        data-theme={theme}
        data-testid="header"
      >
        <div className={styles.wrapper}>
          <div className={styles.container}>
            <Logo
              logo={logo}
              mobileLogo={mobileLogo}
              type={theme}
              isSlim={useSlimHeader}
            />

            {navigation?.length > 0 && (
              <nav
                className={styles.navigation}
                aria-label="Main navigation"
                data-testid="header-nav"
              >
                <ul className={styles.navList} data-testid="header-nav-list">
                  {navigation.map((navItem, index) => {
                    const hasDropdown = navItem.items?.length > 0;
                    const isDropdownActive = activeDropdown === index;
                    const isFeatured = navItem.featured;

                    return (
                      <li
                        key={navItem.title || `nav-${index}`}
                        className={styles.navItem}
                        data-testid={`header-nav-item-${index}`}
                        onMouseEnter={hasDropdown ? handleNavEnter : undefined}
                        onMouseLeave={hasDropdown ? handleNavLeave : undefined}
                      >
                        <a
                          href={navItem.link || '#'}
                          id={navItem.link ? undefined : `nav-${index}`}
                          className={`${styles.navLink} ${
                            isFeatured ? styles.navLinkFeatured : ''
                          } ${isDropdownActive ? styles.navLinkActive : ''}`}
                          data-testid={`header-nav-link-${index}`}
                          role={hasDropdown ? 'button' : undefined}
                          aria-haspopup={hasDropdown ? 'true' : undefined}
                          aria-expanded={
                            hasDropdown ? isDropdownActive : undefined
                          }
                          onMouseEnter={() =>
                            hasDropdown && handleNavHover(index)
                          }
                          onMouseLeave={
                            hasDropdown ? handleNavLeave : undefined
                          }
                          onKeyDown={(e) =>
                            handleNavKeyDown(e, index, hasDropdown)
                          }
                          ref={(el) => {
                            navRefs.current[index] = el;
                          }}
                          onClick={(e) => {
                            if (!navItem.link) {
                              e.preventDefault();
                            }
                          }}
                        >
                          {navItem.title}
                        </a>
                        {hasDropdown && (
                          <Dropdown
                            items={navItem.items}
                            isOpen={isDropdownActive}
                            onMouseEnter={handleNavEnter}
                            onMouseLeave={handleNavLeave}
                            onItemClick={handleDropdownItemClick}
                            onClose={() => {
                              openedViaKeyboard.current = false;
                              setActiveDropdown(null);
                              if (navRefs.current[index]) {
                                navRefs.current[index].focus();
                              }
                            }}
                            triggerId={`nav-${index}`}
                            shouldFocusOnOpen={openedViaKeyboard.current}
                            buttonRef={navRefs.current[index]}
                            isSlimHeader={useSlimHeader}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </nav>
            )}

            {signInData && (
              <div className={styles.signIn} data-testid="header-sign-in">
                <SignIn
                  signInData={signInData}
                  isSubMenuOpen={isMenuOpen}
                  onDropdownStateChange={setIsSignInDropdownOpen}
                />
              </div>
            )}

            {navigation?.length > 0 && (
              <button
                type="button"
                className={styles.hamburger}
                onClick={toggleMenu}
                aria-expanded={isMenuOpen}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                data-testid="header-hamburger"
              >
                <img
                  src={
                    isMenuOpen
                      ? CloseIcon.src || CloseIcon
                      : HamburgerIcon.src || HamburgerIcon
                  }
                  alt={isMenuOpen ? 'Close' : 'Menu'}
                  width={
                    isMenuOpen
                      ? ICON_DIMENSIONS.CLOSE_WIDTH
                      : ICON_DIMENSIONS.HAMBURGER_WIDTH
                  }
                  height={
                    isMenuOpen
                      ? ICON_DIMENSIONS.CLOSE_HEIGHT
                      : ICON_DIMENSIONS.HAMBURGER_HEIGHT
                  }
                />
              </button>
            )}
          </div>
        </div>
      </header>

      <div
        className={classNames(styles.contentPadding, {
          [styles.contentPaddingSlim]: useSlimHeader,
        })}
      />

      {isMenuOpen && (
        <nav
          className={classNames(styles.mobileMenu, {
            [styles.mobileMenuSlim]: useSlimHeader,
          })}
          aria-label="Mobile navigation"
          data-testid="header-mobile-menu"
        >
          <ul
            className={styles.mobileNavList}
            data-testid="header-mobile-nav-list"
          >
            {navigation.map((navItem, index) => {
              const hasSubmenu = navItem.items?.length > 0;
              const isSubmenuOpen = activeMobileSubmenu === index;
              const submenuId = `mobile-submenu-${index}`;

              return (
                <li
                  key={navItem.title || `mobile-nav-${index}`}
                  className={styles.mobileNavItem}
                  data-testid={`header-mobile-nav-item-${index}`}
                >
                  {navItem.link ? (
                    <a
                      href={navItem.link}
                      className={styles.mobileNavLink}
                      data-testid={`header-mobile-nav-link-${index}`}
                      onClick={() => handleMobileMenuClick(navItem.title)}
                    >
                      {navItem.title}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.mobileNavLink} ${
                        isSubmenuOpen ? styles.mobileNavLinkOpen : ''
                      }`}
                      data-testid={`header-mobile-nav-link-${index}`}
                      onClick={() =>
                        setActiveMobileSubmenu(isSubmenuOpen ? null : index)
                      }
                      aria-expanded={hasSubmenu ? isSubmenuOpen : undefined}
                      aria-controls={hasSubmenu ? submenuId : undefined}
                    >
                      {navItem.title}
                      {hasSubmenu && (
                        <img
                          src={DropdownArrow.src || DropdownArrow}
                          alt=""
                          className={styles.dropdownArrow}
                        />
                      )}
                    </button>
                  )}
                  {hasSubmenu && isSubmenuOpen && (
                    <div
                      className={styles.mobileSubMenuWrapper}
                      id={submenuId}
                      data-testid={`header-mobile-submenu-${index}`}
                    >
                      {navItem.items.map((section, sectionIndex) => (
                        <ul
                          key={`section-${navItem.title}-${
                            section.items?.[0]?.label ||
                            section.items?.[0]?.link ||
                            `idx-${sectionIndex}`
                          }`}
                          className={`${styles.mobileSubMenuList} ${
                            sectionIndex > 0 ? styles.mobileSubMenuSection : ''
                          }`}
                        >
                          {section.items?.map((item, itemIndex) => {
                            if (!item.label) return null;
                            return (
                              <li key={item.link || item.label}>
                                <a
                                  href={item.link}
                                  className={`${styles.mobileSubMenuItem} ${
                                    item.highlighted
                                      ? styles.mobileSubMenuHighlighted
                                      : ''
                                  }`}
                                  data-testid={`header-mobile-submenu-item-${index}-${sectionIndex}-${itemIndex}`}
                                  onClick={() =>
                                    handleMobileMenuClick(item.label)
                                  }
                                >
                                  {item.label}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </>
  );
}

Header.propTypes = {
  logo: PropTypes.object,
  mobileLogo: PropTypes.object,
  navigation: PropTypes.array,
  signInData: PropTypes.object,
  theme: PropTypes.string,
  useSlimHeader: PropTypes.bool,
};
